// Trading — "what did I buy at, what did I sell at, and what did that earn".
//
// Distinct from Profit, deliberately. Profit answers `income − expenses` from
// account balances. This answers a different question from the DEAL side: per
// trade, what the moving-average cost was, what it sold for, and the margin
// between them — grouped per day / week / month / year / all time.
//
// A report, not a workflow: read-only, dense, no stray buttons.
//
// ── THE RULE THIS LAYOUT EXISTS TO KEEP VISIBLE ─────────────────────────────
// AED and INR are NEVER summed. The desk runs two books with separate cost
// bases ("INR & AED Trades keep seperate") and there is no FX feed, so a single
// blended margin would be an invented rate wearing a confident face. Currencies
// are separate rows, always, at every zoom level.
//
// Trading margin and cheque fees are also kept apart: a manager's cheque earns
// a FEE, not a margin on inventory. Merging them flatters trading performance
// with income that says nothing about how well the desk bought and sold.
//
// A margin the engine did not realise reads "—", never 0.00. A customer SALE
// to the desk realises nothing (it builds inventory), and a sale from a book
// with no cost basis defers rather than costing at zero. "0.00" would claim the
// desk sold at cost.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.25): the rules above are said once, in "About
// this screen" (they were on the subtitle, the footer and the mix paragraph);
// the words are the customer's ("Customer bought / Customer sold", as Home,
// Trades and Book trade); the bps column is headed "Margin bps"; a book reads
// "USDT · AED"; the bucket is a "Group by" chip row (a different axis from the
// period window); the tables are the shared ReportTable on the palette's
// tokens, and the phone gets one card per row instead of an eight-column
// table scrolling sideways.
//
// 21 Oct 2026 audit (operator, overnight: "I need clean view for operator"): Mix
// and By party are labelled "All time" — the route reads them over every deal
// whatever "Group by" says, so under "Month" they read as monthly; "incl. short
// cover" no longer pushes the margin out of its right-aligned column (the words
// stand under the figure); a quiet period is ONE "Nothing in this period." and
// not three identical boxes; a phone card's figures sit on one grid, so the same
// word starts at the same x on every card; the subtitle that restated the
// heading is gone (its words are the first line of "About this screen").
//
// 21 Oct 2026 design-system sweep: a failed read is a red line with "Try again" under the
// title and the Group-by chips — never the quiet period's empty line; an empty section,
// and a quiet period, are the kit's one empty line (a section drew a table box for the
// console and another for the phone).
//
// 19 Sept 2026 (the phone pass): below lg each report is one block per book
// (or per currency) under the Ledger's block header, as Profit, Trades and
// Money stand — the book named once at the top, never on every row — and
// each row is a Ledger card: line 1 the period / type / party and the margin
// at the right, line 2 the figures behind it with a muted word beside each.
// The party dropdown is the control it was, full width on the phone. The lg+
// tables and filters are unchanged.

import { useState } from 'react';
import type { ReactNode } from 'react';
import { readWords } from '../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { EmptyState, Pending, StateLine } from '../components/kit';
import { ReportTable, type ReportColumn, type ReportRow } from '../components/ReportTable';
import { Chip, FilterGroup } from '../components/FilterRail';
import { LEDGER, LedgerHead } from '../components/Ledger';
import { partyName } from '../lib/naming';
import type {
  TradingReportResponse, TradingSeriesRow, TradingMixRow, TradingPartyRow,
} from '../lib/api';
import { displayAsset, fmtAmount, fmtDay, fmtMonth, fmtRate, booksClock } from '../lib/format';

type Bucket = 'day' | 'week' | 'month' | 'year' | 'all';

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'day',   label: 'Day' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year',  label: 'Year' },
  { key: 'all',   label: 'All time' },
];

/** Margin in basis points of the money that moved — the comparable number
 *  across corridors, since 30 AED and 1,900 INR are not comparable amounts. */
function marginBps(margin: string | null, volume: string): string {
  if (margin === null) return '—';
  const m = Number(margin), v = Number(volume);
  if (!v) return '—';
  return ((m / v) * 10000).toFixed(1);
}

function periodLabel(p: string | null, bucket: Bucket): string {
  if (!p) return 'All time';
  if (bucket === 'day')   return fmtDay(p, true);
  if (bucket === 'week')  return `Week of ${fmtDay(p)}`;
  if (bucket === 'month') return fmtMonth(p);
  if (bucket === 'year')  return String(booksClock(p)?.y ?? p);
  return p;
}

/** The mix rows in the customer's words (§6 "Trade direction"): the engine's
 *  desk_sell is the customer buying. */
const CLASS_WORDS: Record<string, string> = {
  desk_sell: 'Customer bought',
  desk_buy:  'Customer sold',
  delivery:  'Cheque (fee)',
};

/** A margin figure: "—" when the engine realised none, the palette's tokens
 *  for the sign, and the short-cover flag where it applies. `bps`, on the
 *  phone card, rides muted beside the figure where the table has a column. */
function Margin({ value, ccy, shortCover, bps, className = '' }: {
  value: string | null; ccy: string; shortCover?: string | null; bps?: string; className?: string;
}) {
  if (value === null) return <span className="text-tg-hint">—</span>;
  const n = Number(value);
  return (
    <span className={`font-mono tabular ${n < 0 ? 'text-neg' : n > 0 ? 'text-pos' : ''} ${className}`}>
      {fmtAmount(value, ccy)}
      {bps && bps !== '—' && (
        <span className="ml-1.5 text-meta text-tg-hint font-normal whitespace-nowrap">
          {bps}<span className="font-sans"> bps</span>
        </span>
      )}
      {/* Under the figure, right-aligned like it: inline beside it the words were
          wider than the figure and pushed it out of its column (21 Oct 2026 audit). */}
      {shortCover && Number(shortCover) !== 0 && (
        <span className="block text-right text-meta leading-tight text-tg-hint font-sans font-normal" title="realised by a buy covering a short position">
          incl. short cover
        </span>
      )}
    </span>
  );
}

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

/** A read that failed, as a line of its own: what failed, the first sentence of what the books said, the rest behind a
 *  tap, and the way out. */
function ReadFailed({ what, error, onRetry }: { what: string; error: unknown; onRetry: () => void }) {
  const words = readWords(error);
  const first = words.split(/(?<=[.!?])\s+/)[0] ?? words;
  return (
    <StateLine
      tone="bad"
      more={words.slice(first.length).trim() || undefined}
      action={<button type="button" className="btn-link" onClick={onRetry}>Try again</button>}
    >
      Couldn&rsquo;t read {what}. {first}
    </StateLine>
  );
}

/** The word after a deal count on a phone card — "1 deal", "12 deals". */
const dealsWord = (n: string) => (Number(n) === 1 ? 'deal' : 'deals');
/** A deal count in thousands, as every number on the books ("1,230"; the Worker sends it as a string). */
const dealCount = (n: string) => Number(n).toLocaleString('en-US');

export function TradingReport() {
  const [bucket, setBucket] = useState<Bucket>('day');
  const [party, setParty] = useState<string>('');

  const { data, isLoading, error, refetch } = useQuery<TradingReportResponse>({
    queryKey: ['trading-report', bucket, party],
    queryFn: () => api.tradingReport({ bucket, ...(party ? { party } : {}) }),
  });

  const parties = [...new Set((data?.by_party ?? []).map((p) => p.party_slug))].sort();

  return (
    <div className="space-y-4 lg:space-y-6">
      <ScreenHeader
        title="Trading"
        help={
          <>
            <p>
              Per deal: the moving-average cost of what was sold, what it sold for, and the margin
              between them — grouped by day, week, month, year or all time.
            </p>
            <p>
              AED and INR are never summed. The desk runs two books with separate costs and there is
              no FX feed, so a blended margin would be an invented rate.
            </p>
            <p>
              A cheque earns a fee, not a margin on inventory — the two are kept apart so the trading
              figures say how well the desk traded.
            </p>
            <p>
              Share of volume and share of margin answer different questions and often disagree — a
              type can be most of the throughput and little of the money. A customer sale to the desk
              normally realises no margin: it builds inventory, and buying below the average lowers the
              average so the gain lands when the stock is sold. The exception is a buy that covers a
              short, which does realise and is flagged where it happens.
            </p>
            <p>A margin the engine did not realise reads &ldquo;—&rdquo;, never 0.00.</p>
            {data?.note && <p>{data.note}</p>}
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-x-5 gap-y-2">
          <FilterGroup label="Group by">
            {BUCKETS.map((b) => (
              <Chip key={b.key} active={bucket === b.key} onClick={() => setBucket(b.key)}>{b.label}</Chip>
            ))}
          </FilterGroup>
          {/* The party dropdown: the whole line on the phone, its own width
              at lg+ (the control itself is as it was — 14px, the panel face). */}
          {parties.length > 0 && (
            <select
              value={party}
              onChange={(e) => setParty(e.target.value)}
              className="w-full lg:w-auto rounded-lg border border-stroke bg-panel px-3 py-1.5 text-field lg:text-body text-tg-text"
            >
              <option value="">All parties</option>
              {parties.map((p) => <option key={p} value={p}>{partyName({ slug: p })}</option>)}
            </select>
          )}
        </div>
      </ScreenHeader>

      {isLoading && <Pending />}
      {/* A FAILED READ is a line of its own (the sweep, 21 Oct 2026) — never the quiet period's "Nothing in this
          period": what failed, the first sentence of what the books said ("the books did not answer", "your
          sign-in has expired"), the rest behind a tap, and the way out. */}
      {error && <ReadFailed what="trading" error={error} onRetry={() => refetch()} />}

      {/* A quiet period is claimed only when the read did NOT fail: a refresh that failed over a cached empty answer shows the red
          line alone (Astra, 22 Oct). */}
      {data && (
        // A quiet period says so ONCE — three sections each saying "Nothing in this
        // period." (and three headings over them) was the one fact three times. And only for a read that
        // SUCCEEDED: after a failed refresh the cached sections that hold rows stay, and a section that is empty
        // in the cache claims nothing (Astra, 22 Oct — rounds one and two).
        data.series.length === 0 && data.mix.length === 0 && data.by_party.length === 0
          ? (error ? null : <EmptyState>Nothing in this period.</EmptyState>)
          : (
            <>
              <Series rows={data.series} bucket={data.bucket} failed={!!error} />
              <Mix rows={data.mix} failed={!!error} />
              <ByParty rows={data.by_party} failed={!!error} />
            </>
          )
      )}
    </div>
  );
}

/* ── one shape for every table: ReportTable at lg+, blocks of cards below ── */

function Report({ title, scope, columns, rows, blocks, failed = false }: {
  title: string;
  /** The read that filled this section failed on its last refresh: an empty section claims nothing (it is drawn not at all). */
  failed?: boolean;
  /** The window a section is read over when it is NOT the "Group by" one: Mix
   *  and By party are all-time whatever the chips say. */
  scope?: string;
  columns: ReportColumn[]; rows: ReportRow[]; blocks: ReactNode;
}) {
  if (failed && rows.length === 0) return null;
  return (
    <section>
      {/* On the phone the title stands at the blocks' own inset (the block's
          px-2 and the LedgerHead's px-2), on one x with the book's name. */}
      <h2 className="text-body font-medium text-tg-hint mb-2 px-4 lg:px-0">
        {title}{scope && <span className="font-normal"> · {scope}</span>}
      </h2>
      {rows.length === 0 ? <EmptyState>Nothing in this period.</EmptyState> : (
        <>
          <div className="hidden lg:block">
            <ReportTable columns={columns} rows={rows} />
          </div>
          <div className="lg:hidden">{blocks}</div>
        </>
      )}
    </section>
  );
}

/** The rows of one report gathered under the book or currency they are in,
 *  in first-seen order — AED and INR never share a block, as they never
 *  share a row. Grouping only: every row stays, in the order the table has. */
function groupRows<T>(rows: T[], keyOf: (r: T) => string): Array<{ key: string; first: T; rows: T[] }> {
  const out: Array<{ key: string; first: T; rows: T[] }> = [];
  const bags = new Map<string, T[]>();
  for (const r of rows) {
    const k = keyOf(r);
    let bag = bags.get(k);
    if (!bag) { bag = []; bags.set(k, bag); out.push({ key: k, first: r, rows: bag }); }
    bag.push(r);
  }
  return out;
}

/** One block on the phone: the Ledger's block header naming the book or the
 *  currency once, then one card per row under it — the same section every
 *  currency block on Profit, Trades and Money stands in. */
function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="px-2 pt-3 pb-1 first:pt-0">
      <LedgerHead title={title} />
      {children}
    </section>
  );
}

/** One row as a phone card on the Ledger's card seats: line 1 the words and,
 *  at the right, the margin — the figure this report exists for; line 2 the
 *  figures behind it, wrapping as they must, a muted word beside each. */
function RowCard({ words, margin, figures, seats = true }: {
  words: ReactNode; margin: ReactNode; figures: ReactNode;
  /** Two seats a row (the default). False: one wrapping line, as a card of three
   *  short figures (the mix) always was — two seats would make it a second row. */
  seats?: boolean;
}) {
  return (
    <div className={`${LEDGER.card} px-2`}>
      <div className={LEDGER.cardLine1}>
        <span className="flex-1 min-w-0">{words}</span>
        <span className="text-right">{margin}</span>
      </div>
      {/* Two seats a row, so a word ("Volume", "Avg sell") starts at the same x on
          every card — wrapped flex put it wherever the figure before it ended. */}
      <div className={seats
        ? 'mt-1 grid grid-cols-2 items-baseline gap-x-3 gap-y-0.5 text-meta'
        : `${LEDGER.cardLine2} flex-wrap gap-y-0.5`}>
        {figures}
      </div>
    </div>
  );
}

/** A figure on a card's second line: the number in .amt, its word muted
 *  beside it — "Volume 1,234.00", "12 deals", "45.0% of volume". */
function Fig({ label, value, after }: { label?: string; value: ReactNode; after?: string }) {
  return (
    <span className="whitespace-nowrap">
      {label && <span className="text-tg-hint">{label} </span>}
      <span className="amt">{value}</span>
      {after && <span className="text-tg-hint"> {after}</span>}
    </span>
  );
}

const book = (asset: string, ccy: string) => `${displayAsset(asset)} · ${displayAsset(ccy)}`;

function Series({ rows, bucket, failed = false }: { rows: TradingSeriesRow[]; bucket: Bucket; failed?: boolean }) {
  const columns: ReportColumn[] = [
    { key: 'period', label: 'Period', align: 'left' },
    { key: 'book',   label: 'Book', align: 'left' },
    { key: 'deals',  label: 'Deals' },
    { key: 'volume', label: 'Volume' },
    { key: 'cost',   label: 'Avg cost' },
    { key: 'sell',   label: 'Avg sell' },
    { key: 'margin', label: 'Margin' },
    { key: 'bps',    label: 'Margin bps' },
  ];
  const table: ReportRow[] = rows.map((r, i) => ({
    key: `${r.period ?? 'all'}-${r.inv_asset}-${r.quote_ccy}-${i}`,
    cells: {
      period: periodLabel(r.period, bucket),
      book:   <span className="text-tg-hint">{book(r.inv_asset, r.quote_ccy)}</span>,
      deals:  dealCount(r.deals),
      volume: fmtAmount(r.volume_quote, r.quote_ccy),
      cost:   <span className="text-tg-hint">{fmtRate(r.avg_cost_rate, r.quote_ccy)}</span>,
      sell:   <span className="text-tg-hint">{fmtRate(r.avg_sell_rate, r.quote_ccy)}</span>,
      margin: <Margin value={r.trading_margin} ccy={r.quote_ccy} shortCover={r.short_cover_margin} />,
      bps:    <span className="text-tg-hint">{marginBps(r.trading_margin, r.volume_quote)}</span>,
    },
  }));
  // The phone: one block per book — "USDT · AED" named once at its head —
  // and a card per period inside it.
  const blocks = groupRows(rows, (r) => `${r.inv_asset}|${r.quote_ccy}`).map((g) => (
    <Block key={g.key} title={book(g.first.inv_asset, g.first.quote_ccy)}>
      {g.rows.map((r, i) => (
        <RowCard
          key={`${r.period ?? 'all'}-${i}`}
          words={periodLabel(r.period, bucket)}
          margin={
            <Margin
              className="amt"
              value={r.trading_margin}
              ccy={r.quote_ccy}
              shortCover={r.short_cover_margin}
              bps={marginBps(r.trading_margin, r.volume_quote)}
            />
          }
          figures={
            <>
              <Fig value={dealCount(r.deals)} after={dealsWord(r.deals)} />
              <Fig label="Volume" value={fmtAmount(r.volume_quote, r.quote_ccy)} />
              {/* A figure the row has — "Avg cost —" on every row of a book
                  with no buys was a label beside a dash (21 Sept 2026). Each
                  keeps its own seat on the card's grid (cost left, sell right):
                  with no cost the sell stays on the right, an empty seat
                  beside it, so "Avg sell" starts at one x on every card. */}
              {r.avg_cost_rate != null
                ? <Fig label="Avg cost" value={fmtRate(r.avg_cost_rate, r.quote_ccy)} />
                : r.avg_sell_rate != null && <span aria-hidden />}
              {r.avg_sell_rate != null && <Fig label="Avg sell" value={fmtRate(r.avg_sell_rate, r.quote_ccy)} />}
            </>
          }
        />
      ))}
    </Block>
  ));
  return <Report title="Cost, price and margin" columns={columns} rows={table} blocks={blocks} failed={failed} />;
}

function Mix({ rows, failed = false }: { rows: TradingMixRow[]; failed?: boolean }) {
  const columns: ReportColumn[] = [
    { key: 'type',     label: 'Type', align: 'left' },
    { key: 'currency', label: 'Currency', align: 'left' },
    { key: 'deals',    label: 'Deals' },
    { key: 'vol',      label: '% of volume' },
    { key: 'mgn',      label: '% of margin' },
    { key: 'margin',   label: 'Margin' },
  ];
  const words = (m: TradingMixRow) => CLASS_WORDS[m.trade_class] ?? m.trade_class;
  const table: ReportRow[] = rows.map((m, i) => ({
    key: `${m.trade_class}-${m.quote_ccy}-${i}`,
    cells: {
      type:     words(m),
      currency: <span className="text-tg-hint">{displayAsset(m.quote_ccy)}</span>,
      deals:    dealCount(m.deals),
      vol:      <span className="text-tg-hint">{pct(m.share_of_volume_pct)}</span>,
      mgn:      <span className="text-tg-hint">{pct(m.share_of_margin_pct)}</span>,
      margin:   <Margin value={m.margin} ccy={m.quote_ccy} />,
    },
  }));
  // The phone: one block per currency, a card per trade type inside it.
  const blocks = groupRows(rows, (m) => m.quote_ccy).map((g) => (
    <Block key={g.key} title={displayAsset(g.key)}>
      {g.rows.map((m, i) => (
        <RowCard
          key={`${m.trade_class}-${i}`}
          seats={false}
          words={words(m)}
          margin={<Margin className="amt" value={m.margin} ccy={m.quote_ccy} />}
          figures={
            <>
              <Fig value={dealCount(m.deals)} after={dealsWord(m.deals)} />
              <Fig value={pct(m.share_of_volume_pct)} after="of volume" />
              <Fig value={pct(m.share_of_margin_pct)} after="of margin" />
            </>
          }
        />
      ))}
    </Block>
  ));
  return <Report title="Mix by trade type" scope="All time" columns={columns} rows={table} blocks={blocks} failed={failed} />;
}

function ByParty({ rows, failed = false }: { rows: TradingPartyRow[]; failed?: boolean }) {
  const columns: ReportColumn[] = [
    { key: 'party',    label: 'Party', align: 'left' },
    { key: 'currency', label: 'Currency', align: 'left' },
    { key: 'deals',    label: 'Deals' },
    { key: 'volume',   label: 'Volume' },
    { key: 'margin',   label: 'Margin' },
  ];
  const ownTrades = (p: TradingPartyRow) =>
    p.party_slug === 'unnamed-legacy' && <span className="ml-2 text-meta text-tg-hint font-normal">(own trades)</span>;
  const table: ReportRow[] = rows.map((p, i) => ({
    key: `${p.party_slug}-${p.quote_ccy}-${i}`,
    cells: {
      party:    <>{partyName({ slug: p.party_slug })}{ownTrades(p)}</>,
      currency: <span className="text-tg-hint">{displayAsset(p.quote_ccy)}</span>,
      deals:    dealCount(p.deals),
      volume:   fmtAmount(p.volume_quote, p.quote_ccy),
      margin:   <Margin value={p.margin} ccy={p.quote_ccy} />,
    },
  }));
  // The phone: one block per currency, a card per party inside it — the
  // name in the party face (.who), as on every list of parties.
  const blocks = groupRows(rows, (p) => p.quote_ccy).map((g) => (
    <Block key={g.key} title={displayAsset(g.key)}>
      {g.rows.map((p, i) => (
        <RowCard
          key={`${p.party_slug}-${i}`}
          words={<><span className="who">{partyName({ slug: p.party_slug })}</span>{ownTrades(p)}</>}
          margin={<Margin className="amt" value={p.margin} ccy={p.quote_ccy} />}
          figures={
            <>
              <Fig value={dealCount(p.deals)} after={dealsWord(p.deals)} />
              <Fig label="Volume" value={fmtAmount(p.volume_quote, p.quote_ccy)} />
            </>
          }
        />
      ))}
    </Block>
  ));
  return <Report title="By party" scope="All time" columns={columns} rows={table} blocks={blocks} failed={failed} />;
}
